import Analytics from '@opencare/analytics';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useHistory } from 'react-router-dom';

import { BRAND_COLORS } from 'brand-guidelines/colors';
import { TooltipIcon } from 'common/components/TooltipWithIcon/TooltipWithIcon';
import { selectDataClinicId } from 'connect/selectors';
import { CheckoutTimerStateHandler } from 'connect/state/handlers/checkout-timer';

import { selectDataClinicIsInstantBook } from 'pages/profile/connect/selectors';
import { useAnalyticsService } from 'services/AnalyticsService';
import { useCountdown } from 'utils/customHooks/useCountdown';

import { Text, Wrapper } from './CheckoutTimer-styled-components';
import { CheckoutTimerEndModal } from './CheckoutTimerEndModal';
import { CHECKOUT_TIMER_STATUSES, DEFAULT_CHECKOUT_TIMER_DATA } from './constants';

const formatTime = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
};

const { ENDED, RUNNING, CLEARED } = CHECKOUT_TIMER_STATUSES;

const analytics = new Analytics();

export const CheckoutTimer = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const analyticsService = useAnalyticsService();

  const clinicId = useSelector(selectDataClinicId);
  const clinicIsInstantBook = useSelector(selectDataClinicIsInstantBook);
  const checkoutTimerData = useSelector(CheckoutTimerStateHandler.selector);
  const showTimerEndModal = useMemo(() => checkoutTimerData?.status === ENDED, [checkoutTimerData?.status]);

  const [resetTrigger, setResetTrigger] = useState(false);
  const [timeLeft, setTimeLeft] = useState(DEFAULT_CHECKOUT_TIMER_DATA.timeLeft);
  const formattedTimeLeft = useMemo(() => formatTime(timeLeft), [timeLeft]);

  const handleTick = useCallback((timeLeft: number) => {
    setTimeLeft(timeLeft);
    dispatch(
      CheckoutTimerStateHandler.update({
        timeLeft,
        status: timeLeft > 0 ? RUNNING : ENDED,
      }),
    );
  }, [dispatch]);

  useCountdown({
    initialTime: DEFAULT_CHECKOUT_TIMER_DATA.timeLeft,
    resetTrigger,
    onTick: handleTick,
  });

  const resetTimer = useCallback(() => {
    // Toggle resetTrigger to reset the countdown
    setResetTrigger((prev) => !prev);
    dispatch(
      CheckoutTimerStateHandler.update({
        ...DEFAULT_CHECKOUT_TIMER_DATA,
        status: CLEARED,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (checkoutTimerData) {
      return;
    }

    dispatch(CheckoutTimerStateHandler.update(DEFAULT_CHECKOUT_TIMER_DATA));
  }, [checkoutTimerData, dispatch]);

  const returnToPracticeProfile = useCallback((source: string) => {
    resetTimer();
    history.push(`/results/${clinicId}`);
    analytics.trackClick({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'form-booking-checkout-timer-end',
      name: 'return-to-practice-profile',
      section: source || 'unknown',
      ...analyticsService.analyticsData,
    });
  }, [clinicId, history, resetTimer, analyticsService.analyticsData]);

  const continueWithRequest = useCallback(() => {
    resetTimer();
    analytics.trackClick({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'form-booking-checkout-timer-end',
      name: 'continue-with-request',
      ...analyticsService.analyticsData,
    });
  }, [resetTimer, analyticsService.analyticsData]);

  return (
    <>
      {showTimerEndModal
        && (
          <CheckoutTimerEndModal
            clinicIsInstantBook={clinicIsInstantBook}
            handleClose={returnToPracticeProfile}
            handleContinue={continueWithRequest}
          />
        )}

      <Wrapper>
        <Text>Holding your timeslot for {formattedTimeLeft}</Text>

        <TooltipIcon iconColor={BRAND_COLORS.red}>
          We are holding your time slot for you.
          You have to complete the booking within the time limit.
          If you do not complete the booking within the time limit, the slot will be released for others.
        </TooltipIcon>
      </Wrapper>
    </>
  );
};
